// @flow

import type {
  ActionHandlers,
  Reducer,
  Selector,
} from '@travel-frontend/state-handling-sdk';
import type {FetchCustomerSuccess} from 'redux/v1/customer/data/actions';
import type {PutCustomerSuccess} from 'redux/v1/customer/put/actions';
import type {FetchJourneySuccess} from 'redux/v1/journey/actions';
import type {FetchTravelersSuccess} from 'redux/v1/travelers/data/actions';
import type {FormState} from 'redux/v1/travelers/form/FormState';
import type {TravelerForm} from 'redux/v1/travelers/form/models';
import type {PutTravelersError} from 'redux/v1/travelers/put/actions';

import {
  createReducerUsingActionHandlers,
  ERROR,
  PURE,
  SUCCESS,
} from '@travel-frontend/state-handling-sdk';
import {createSelector} from 'reselect';

import {CUSTOMER_IS_TRAVELER_CHECKBOX} from 'consts';
import {FETCH_CUSTOMER} from 'redux/v1/customer/data/actions';
import {PUT_CUSTOMER} from 'redux/v1/customer/put/actions';
import {FETCH_JOURNEY} from 'redux/v1/journey/actions';
import {FETCH_TRAVELERS} from 'redux/v1/travelers/data/actions';
import {
  CREATE_TRAVELER_FORMS,
  SET_CUSTOMER_IS_TRAVELER,
  SET_TRAVELER_ERRORS,
  SUBMIT_TRAVELER_FORM,
} from 'redux/v1/travelers/form/actions';
import {
  createTravelerForms,
  isTravelerNotEmpty,
  mapCustomer,
  mapTravelers,
  parseTravelerErrors,
} from 'redux/v1/travelers/form/utils';
import {PUT_TRAVELERS} from 'redux/v1/travelers/put/actions';
import {formState} from 'redux/v1/travelers/TravelersState';
import {formatISOtoDateString} from 'utils';

export const initialState: FormState = {
  forms: [],
  customerIsTraveler: undefined,
};

const actionCreators: ActionHandlers<FormState> = {
  [PURE(SET_CUSTOMER_IS_TRAVELER)]: (state: FormState, action) => {
    const customerIsTravelerSessionStorageObject: {
      journeyId: string,
      isChecked: boolean,
      curRoomIndex: number,
    } = {
      journeyId: action.journeyId,
      curRoomIndex: action.curRoomIndex,
      isChecked: false,
    };

    const auxForms = state.forms.map((form, index) => {
      if (
        index === action.travelerIndex &&
        action.checkedTravelerIndex !== undefined
      ) {
        customerIsTravelerSessionStorageObject.isChecked = true;
        sessionStorage.setItem(
          CUSTOMER_IS_TRAVELER_CHECKBOX,
          JSON.stringify(customerIsTravelerSessionStorageObject)
        );

        return {
          data: {
            ...state.customer.data,
            id: form.data.id,
          },
          isValid: state.customer.isValid,
        };
      } else if (
        index === action.travelerIndex &&
        action.checkedTravelerIndex === undefined
      ) {
        customerIsTravelerSessionStorageObject.isChecked = false;
        sessionStorage.setItem(
          CUSTOMER_IS_TRAVELER_CHECKBOX,
          JSON.stringify(customerIsTravelerSessionStorageObject)
        );

        return {
          data: {
            id: form.data.id,
            title: '',
            firstName: '',
            lastName: '',
            birthday: '',
          },
          isValid: false,
        };
      }
      return form;
    });

    return {
      ...state,
      customerIsTraveler: action.checkedTravelerIndex,
      forms: Array.from(auxForms),
    };
  },
  [SUCCESS(FETCH_TRAVELERS)]: (
    state: FormState,
    action: FetchTravelersSuccess
  ) => ({
    ...state,
    forms: action.data === undefined ? [] : mapTravelers(action.data),
  }),
  [ERROR(PUT_TRAVELERS)]: (state: FormState, action: PutTravelersError) => {
    const auxErrors = parseTravelerErrors(action.error);
    const auxForm = state.forms.map((form, index) => {
      if (auxErrors[index]) {
        return {
          ...form,
          errors: {...auxErrors[index]},
          isValid: false,
        };
      }
      return form;
    });
    return {
      ...state,
      forms: Array.from(auxForm),
    };
  },
  [PURE(SUBMIT_TRAVELER_FORM)]: (state: FormState, action) => {
    const isValid = isTravelerNotEmpty(action.form);
    const auxForm = state.forms.map((form, index) => {
      if (index === action.index) {
        return {
          data: {...action.form},
          isValid,
        };
      }
      return form;
    });

    return {
      ...state,
      forms: Array.from(auxForm),
    };
  },
  [PURE(SET_TRAVELER_ERRORS)]: (state: FormState, action) => {
    const auxForm = state.forms.map((form, index) => {
      if (index === action.index) {
        const errors = action.errors ? {errors: {...action.errors}} : {};
        return {
          data: {...form.data},
          isValid: false,
          ...errors,
        };
      }
      return form;
    });

    return {
      ...state,
      forms: Array.from(auxForm),
    };
  },
  [SUCCESS(FETCH_CUSTOMER)]: (
    state: FormState,
    action: FetchCustomerSuccess
  ) => {
    const customer: TravelerForm = mapCustomer(action.data);

    if (state.customerIsTraveler !== undefined) {
      const auxForms = state.forms.map((form, index) => {
        if (index === state.customerIsTraveler) {
          return {
            data: {
              ...customer.data,
              birthday: formatISOtoDateString(customer.data.birthday),
            },
            isValid: customer.isValid,
          };
        }
        return form;
      });

      return {
        ...state,
        customer,
        forms: Array.from(auxForms),
      };
    }

    return {
      ...state,
      customer,
    };
  },
  [SUCCESS(PUT_CUSTOMER)]: (state: FormState, action: PutCustomerSuccess) => {
    const customer: TravelerForm = mapCustomer(action.customer);

    if (state.customerIsTraveler !== undefined) {
      const auxForms = state.forms.map((form, index) => {
        if (index === state.customerIsTraveler) {
          return {data: {...customer.data}, isValid: customer.isValid};
        }
        return form;
      });

      return {
        ...state,
        customer,
        forms: Array.from(auxForms),
      };
    }

    return {
      ...state,
      customer,
    };
  },
  [PURE(CREATE_TRAVELER_FORMS)]: (state: FormState, action) => {
    return {
      ...state,
      forms:
        state.forms.length === action.count
          ? Array.from(state.forms)
          : createTravelerForms(
              action.count,
              state.customerIsTraveler === undefined
                ? undefined
                : state.customer,
              state.customerIsTraveler
            ),
    };
  },
  [SUCCESS(FETCH_JOURNEY)]: (
    state: FormState,
    action: FetchJourneySuccess
  ) => ({
    ...state,
    ...(action.data.rooms.length === 1 ? {customerIsTraveler: 0} : {}),
  }),
};

export const formReducer: Reducer<FormState> = // $FlowIgnore
  createReducerUsingActionHandlers<FormState>(initialState, actionCreators);

export const getCustomerIsTraveler: Selector<number | void> = createSelector(
  [formState],
  (state: FormState) => state.customerIsTraveler
);

export const getForms: Selector<TravelerForm[]> = createSelector(
  [formState],
  (state: FormState) => state.forms
);

export const isFormValid: Selector<boolean> = createSelector(
  [formState],
  (state: FormState) => {
    return state.forms.every((form) => form.isValid);
  }
);
